import React, { useCallback, useMemo } from "react";
import z from "zod";

import { Button, Form, Input, Select } from "@skydio/rivet-ui";
import { selectedValue } from "@skydio/shared_ui/components/bits/combobox/store/selectors";

import { useUpdateOidcIdentityProvider } from "hooks/mutation/updateOidcIdentityProvider";
import { displayMutationResults } from "utils/mutations";

import { SsoHelpLink } from "./SsoHelpLink";

interface idp {
  id: string;
  name: string;
  oidcClientId: string | null;
  oidcIssuer: string | null;
  oidcUsePkce: string | null;
}

interface UpdateOidcProviderPaneProps {
  idp: idp;
  onNext?: () => void;
  onCancel?: () => void;
}

const updateOidcSchema = z
  .object({
    name: z.string().max(255, { message: "Name must be less than 255 characters" }).optional(),
    clientId: z.string().optional(),
    clientSecret: z.string().optional(),
    usePkce: z
      .enum(["true", "false"])
      .transform(value => value === "true")
      .optional(),
    wellKnownUrl: z.string().url().or(z.literal("")), // Optional URL, zod makes it annoying
    // providerJson: z.string(), // TODO add file upload when we have text area input for form
  })
  .refine(
    data => {
      return (
        // At least one field must be set
        (data.name !== undefined && data.name.length > 0) ||
        (data.clientId !== undefined && data.clientId.length > 0) ||
        (data.clientSecret !== undefined && data.clientSecret.length > 0) ||
        data.usePkce !== undefined ||
        (data.wellKnownUrl !== undefined && data.wellKnownUrl.length > 0)
      );
    },
    { message: "At least one value must be updated" }
  );

const UpdateOidcProviderPane = ({ idp, onNext, onCancel }: UpdateOidcProviderPaneProps) => {
  const [updateOidcIdentityProvider, loading] = useUpdateOidcIdentityProvider(
    displayMutationResults(response => response.updateOidcIdentityProvider, {
      onSuccess: () => {
        onNext?.();
      },
      successMessage: () => "OIDC provider created successfully",
    })
  );

  const handleSubmit = useCallback(
    (values: z.infer<typeof updateOidcSchema>) => {
      updateOidcIdentityProvider(
        idp.id,
        values.name ? values.name : null,
        values.clientId ? values.clientId : null,
        values.clientSecret ? values.clientSecret : null,
        values.usePkce ?? null,
        values.wellKnownUrl ? values.wellKnownUrl : null,
        null // TODO support file upload once we have text area input for form
      );
    },
    [idp.id, updateOidcIdentityProvider]
  );

  const defaultValues: z.infer<typeof updateOidcSchema> = useMemo(() => {
    return {
      name: undefined,
      clientId: undefined,
      clientSecret: undefined,
      usePkce: undefined,
      wellKnownUrl: "",
    };
  }, []);

  const usePkceItems = useMemo(() => {
    return [
      { label: "No", value: "false" },
      { label: "Yes", value: "true" },
    ];
  }, []);

  const oidcWellKnownDescription = useMemo(
    () => (
      <div>
        URL for OIDC provider metadata e.g.:
        <br />
        https://company.okta.com/.well-known/openid-configuration <br />
        https://login.microsoftonline.com/company/v2.0/.well-known/openid-configuration
      </div>
    ),
    []
  );

  return (
    <Form
      schema={updateOidcSchema}
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      className="flex flex-col items-start gap-2"
    >
      <>
        <div className="flex w-5/6 flex-col gap-4">
          <Input
            name="name"
            label="Name"
            placeholder={idp.name}
            isClearable
            isDisabled={loading}
            description="Name of Identity Provider"
          />
          <Input
            name="clientId"
            label="ClientId"
            placeholder={idp.oidcClientId || "Client ID"}
            isClearable
            isDisabled={loading}
            description="Client ID for integration"
          />
          <Input
            name="clientSecret"
            type="password"
            label="Client Secret"
            placeholder="**********"
            isClearable
            isDisabled={loading}
            description="Client Secret for integration"
          />
          <Select
            name="usePkce"
            label="Use PKCE"
            isDisabled={loading}
            description="Use PKCE for the OIDC provider"
            placeholder={idp.oidcUsePkce === "true" ? "Yes" : "No"}
            items={usePkceItems}
            value={String(selectedValue)}
          >
            {item => <Select.Item key={item.value}>{item.label}</Select.Item>}
          </Select>
          <Input
            name="wellKnownUrl"
            label="Well Known URL"
            placeholder="https://provider.example.com/.well-known/openid-configuration"
            isClearable
            isDisabled={loading}
            description={oidcWellKnownDescription}
          />
        </div>
        <span className="flex w-full justify-between">
          <SsoHelpLink />
          <div className="flex gap-4 self-end">
            <Button onClick={onCancel}>Cancel</Button>
            <Button isLoading={loading} type="submit" id="updateProvider" variant="primary">
              Update
            </Button>
          </div>
        </span>
      </>
    </Form>
  );
};

export default UpdateOidcProviderPane;
